import React, { useState } from "react";
import EditIcon from "../../../../../../Assets/ActionIcons/EditIcon.svg";
import { useSettingsStore } from "../../../../../../Store/Settings";
import { getTextColorBasedOnLuminance } from "../../../../../../Utils/ColorUtils";
import { PaymentEmailBodyView, PaymentsEmailReason } from "../../../../../../Contracts/Settings";
import { toLocalDateString } from "../../../../../../Utils/DateUtils";
import { useAuth0 } from "@auth0/auth0-react";

export interface AnyPaymentEmailPreviewProps {
  brandingCompanyName?: string;
  logoUrl?: string;
  emailLinkColor?: string;
  emailButtonColor?: string;
  paymentsEmailReason: PaymentsEmailReason;
}

const AnyPaymentEmailPreview: React.FC<AnyPaymentEmailPreviewProps> = ({
  brandingCompanyName,
  logoUrl,
  emailLinkColor,
  emailButtonColor,
  paymentsEmailReason
}) => {
  const { isLoading, emailBodyViews, updateEmailBodyViews } = useSettingsStore();
  const [dirtyEmailBodyView, setDirtyEmailBodyView] = useState<PaymentEmailBodyView | undefined>(
    undefined
  );
  const { getAccessTokenSilently } = useAuth0();
  const targetEmailBodyView = emailBodyViews.find(view => view.reason === paymentsEmailReason);
  let emailBodyToShow: PaymentEmailBodyView | undefined;

  if (targetEmailBodyView && targetEmailBodyView.id !== undefined) {
    emailBodyToShow = {
      ...targetEmailBodyView,
      ...dirtyEmailBodyView
    };
  } else {
    emailBodyToShow = undefined;
  }

  const [isEditing, setIsEditing] = useState(false);
  const mergeTagValues: { [key: string]: string } = {
    "{{Recipient.Name}}": "John Doe",
    "{{Invoice.Number}}": "INV-91581",
    "{{Invoice.Amount}}": "$ 1.000,00",
    "{{Invoice.DueDate}}": toLocalDateString(new Date()),
    "{{Company.Name}}": brandingCompanyName || "My Company"
  };

  const saveContent = async () => {
    if (dirtyEmailBodyView) {
      await updateEmailBodyViews(getAccessTokenSilently, dirtyEmailBodyView);
      setDirtyEmailBodyView(undefined);
      setIsEditing(false);
    }
  };

  const renderBodyText = (text?: string) => {
    if (!!text) {
      Object.keys(mergeTagValues).forEach(key => {
        const value = mergeTagValues[key];
        if (text) {
          text = text.replace(new RegExp(key, "g"), value);
        }
      });
      return text.split("\n").map((line, index) => (
        <p key={index} className="my-2">
          {line}
        </p>
      ));
    } else return null;
  };

  const isDirty = dirtyEmailBodyView ? Object.keys(dirtyEmailBodyView).length > 0 : false;

  return (
    <div className={`mt-4 border border-gray-300 ${isLoading ? "cursor-progress" : ""}`}>
      <div className="flex justify-between px-4 py-2 bg-gray-100">
        <div className="flex space-x-2 items-center">
          {logoUrl && logoUrl.length > 0 && (
            <img src={logoUrl} alt="Company Logo" className="h-5 w-5" />
          )}
          <div
            className={`font-semibold ${
              isLoading
                ? "bg-slate-300 animate-pulse min:h-6 min-w-24 rounded-lg text-slate-300"
                : ""
            }`}
          >
            {brandingCompanyName}
          </div>
        </div>
        {isEditing ? (
          <div className="flex space-x-4">
            <button
              className={`${isDirty ? "text-red-600" : "text-gray-500"} ${
                isLoading ? "cursor-progress" : ""
              } ml-2 hover:scale-105 transition-transform hover:text-red-600`}
              onClick={() => {
                setIsEditing(false);
                setDirtyEmailBodyView(undefined);
              }}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              className={`${
                isDirty
                  ? "hover:scale-105 transition-transform"
                  : "cursor-not-allowed text-gray-700"
              } ${isLoading ? "cursor-progress" : ""}`}
              disabled={!isDirty || isLoading}
              onClick={saveContent}
            >
              Save Content
            </button>
          </div>
        ) : (
          <button
            className={`flex space-x-3 items-center  ${
              isLoading ? "cursor-progress" : "hover:scale-105 transition-transform"
            }`}
            onClick={() => setIsEditing(true)}
          >
            <img src={EditIcon} alt="Edit" />
            <span>Edit Content</span>
          </button>
        )}
      </div>
      <div className="flex flex-col p-4 space-y-3 items-start">
        {isEditing ? (
          <input
            className={`w-full border rounded px-3 py-2 border-gray-300 ${
              isLoading ? "bg-slate-100 animate-pulse" : ""
            }`}
            value={emailBodyToShow?.salutation}
            onChange={e =>
              dirtyEmailBodyView
                ? setDirtyEmailBodyView({
                    ...dirtyEmailBodyView,
                    salutation: e.target.value
                  })
                : targetEmailBodyView &&
                  setDirtyEmailBodyView({
                    ...targetEmailBodyView,
                    salutation: e.target.value
                  })
            }
            disabled={isLoading}
          />
        ) : (
          <span
            className={`mb-2 ${
              isLoading
                ? "bg-slate-300 animate-pulse min-h-6 min-w-24 rounded-lg text-slate-300"
                : ""
            }`}
          >
            {renderBodyText(emailBodyToShow?.salutation)}
          </span>
        )}
        {isEditing ? (
          <input
            className={`w-full border rounded px-3 py-2 border-gray-300 ${
              isLoading ? "bg-slate-100 animate-pulse" : ""
            }`}
            value={emailBodyToShow?.subject}
            onChange={e =>
              dirtyEmailBodyView
                ? setDirtyEmailBodyView({
                    ...dirtyEmailBodyView,
                    subject: e.target.value
                  })
                : targetEmailBodyView &&
                  setDirtyEmailBodyView({
                    ...targetEmailBodyView,
                    subject: e.target.value
                  })
            }
            disabled={isLoading}
          />
        ) : (
          <span
            className={`${
              isLoading ? "bg-slate-300 animate-pulse min-h-6 w-full rounded-lg text-slate-300" : ""
            }`}
          >
            {renderBodyText(emailBodyToShow?.subject)}
          </span>
        )}
        {isEditing ? (
          <textarea
            className={`w-full border rounded px-3 py-2 border-gray-300 ${
              isLoading ? "bg-slate-100 animate-pulse" : ""
            }`}
            value={emailBodyToShow?.body}
            onChange={e =>
              dirtyEmailBodyView
                ? setDirtyEmailBodyView({
                    ...dirtyEmailBodyView,
                    body: e.target.value
                  })
                : targetEmailBodyView &&
                  setDirtyEmailBodyView({
                    ...targetEmailBodyView,
                    body: e.target.value
                  })
            }
            disabled={isLoading}
          />
        ) : (
          <span
            className={`${
              isLoading ? "bg-slate-300 animate-pulse min-h-6 w-full rounded-lg text-slate-300" : ""
            }`}
          >
            {renderBodyText(emailBodyToShow?.body)}
          </span>
        )}
        {[
          PaymentsEmailReason.FirstPayment,
          PaymentsEmailReason.PaymentOverdue,
          PaymentsEmailReason.PaymentReminder,
          PaymentsEmailReason.RequestPaymentMethod
        ].includes(paymentsEmailReason) && (
          <>
            <button
              className={`py-2 px-3 rounded ${isLoading ? "bg-slate-300 animate-pulse" : ""}`}
              style={{
                background: emailButtonColor,
                color: getTextColorBasedOnLuminance(emailButtonColor)
              }}
            >
              {paymentsEmailReason === PaymentsEmailReason.RequestPaymentMethod
                ? "Add your preferred payment method"
                : "Pay Now"}
            </button>
            {!isLoading && (
              <>
                <span>
                  Button isn't working? Copy-paste this URL in your browser's address bar:
                </span>

                <div
                  style={{
                    color: emailLinkColor
                  }}
                  className="cursor-pointer"
                >
                  https://zomentum.com/foo/bar/1/?ui=2&view=btop&ver
                </div>
              </>
            )}
          </>
        )}
        {!isLoading && paymentsEmailReason === PaymentsEmailReason.UpcomingPayment && (
          <span>
            If you wish to stop the auto collection of this invoice, you can cancel it from{" "}
            <a
              href="https://example.com"
              onClick={e => e.preventDefault()}
              style={{
                color: emailLinkColor
              }}
              className="cursor-pointer"
            >
              here
            </a>
            .
          </span>
        )}
        {isEditing ? (
          <textarea
            className={`w-full border rounded px-3 py-2 border-gray-300 ${
              isLoading ? "bg-slate-100 animate-pulse" : ""
            }`}
            value={emailBodyToShow?.signature}
            onChange={e =>
              dirtyEmailBodyView
                ? setDirtyEmailBodyView({
                    ...dirtyEmailBodyView,
                    signature: e.target.value
                  })
                : targetEmailBodyView &&
                  setDirtyEmailBodyView({
                    ...targetEmailBodyView,
                    signature: e.target.value
                  })
            }
            disabled={isLoading}
          />
        ) : (
          <span className={`${isLoading ? "bg-slate-300 h-6 w-20 animate-pulse" : ""}`}>
            {renderBodyText(emailBodyToShow?.signature)}
          </span>
        )}
      </div>
      <div className="mt-6 h-8 w-full bg-gray-100" />
    </div>
  );
};

export default AnyPaymentEmailPreview;
